// Local text-only roster import. No network, storage, or third-party parsers.
export const MAX_ROSTER_BYTES = 2 * 1024 * 1024;
export const MAX_ROSTER_ROWS = 10000;
const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim();
const normalized = value => clean(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export const rosterKey = student => `${normalized(student.name)}|${normalized(student.className)}`;
export function decodeRoster(buffer, encoding = 'auto') {
  const bytes = new Uint8Array(buffer);
  if (bytes.byteLength > MAX_ROSTER_BYTES) throw Error('Fichier limité à 2 Mo.');
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) throw Error('Classeur non pris en charge : enregistrez la feuille en CSV ou en texte tabulé.');
  let actual = encoding;
  if (encoding === 'auto') {
    if (bytes[0] === 0xff && bytes[1] === 0xfe) actual = 'utf-16le';
    else if (bytes[0] === 0xfe && bytes[1] === 0xff) actual = 'utf-16be';
    else { try { new TextDecoder('utf-8', { fatal: true }).decode(bytes); actual = 'utf-8'; } catch { actual = 'windows-1252'; } }
  }
  const text = new TextDecoder(actual, { fatal: true }).decode(bytes).replace(/^\uFEFF/, '');
  if (/^\s*<(?:!doctype|html|table|\?xml)/i.test(text)) throw Error('Export HTML ou XML non pris en charge : utilisez le texte CSV/TSV ou collez les cellules depuis un tableur.');
  if (text.includes('\0')) throw Error('Encodage non reconnu. Choisissez UTF-16 ou exportez en CSV UTF-8.');
  return { text, encoding: actual };
}
export function parseDelimited(text, separator = ';') {
  if (![';', ',', '\t'].includes(separator)) throw Error('Séparateur non pris en charge.');
  const rows = []; let row = [], cell = '', quoted = false, closed = false;
  const pushCell = () => { row.push(cell.trim()); cell = ''; closed = false; if (row.length > 100) throw Error('Maximum 100 colonnes.'); };
  const pushRow = () => { pushCell(); if (row.some(Boolean)) rows.push(row); row = []; if (rows.length > MAX_ROSTER_ROWS + 1) throw Error('Maximum 10 000 lignes.'); };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) { if (c === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else { quoted = false; closed = true; } } else cell += c; }
    else if (c === separator) pushCell();
    else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; pushRow(); }
    else if (c === '"' && !cell.trim() && !closed) { cell = ''; quoted = true; }
    else { if (closed && c.trim()) throw Error('CSV incorrect : caractère après un guillemet fermé.'); cell += c; }
  }
  if (quoted) throw Error('CSV incorrect : guillemet non fermé.');
  if (cell || row.length) pushRow();
  return rows;
}
export function detectSeparator(text) {
  let best = ';', score = -1;
  for (const separator of [';', '\t', ',']) {
    try {
      const rows = parseDelimited(text, separator).slice(0, 30);
      const counts = new Map();
      rows.forEach(row => { if (row.length > 1) counts.set(row.length, (counts.get(row.length) || 0) + 1); });
      const candidate = Math.max(0, ...Array.from(counts, ([width, count]) => count * 100 + width));
      if (candidate > score) { score = candidate; best = separator; }
    } catch { /* Try another delimiter; selected parse will report malformed data. */ }
  }
  return best;
}
const aliases = {
  lastName: ['nom', 'nom de famille', 'nom eleve', "nom de l'eleve"],
  firstName: ['prenom', 'prenoms', 'prenom eleve'],
  fullName: ['eleve', 'eleves', 'nom complet', 'nom et prenom', 'nom prenom', 'identite'],
  className: ['classe', 'classes', 'division', 'classe eleve'],
};
export function suggestMapping(headers) {
  const result = { lastName: -1, firstName: -1, fullName: -1, className: -1 };
  for (const [field, names] of Object.entries(aliases)) result[field] = headers.findIndex(header => names.includes(normalized(header).replace(/[’]/g, "'").replace(/[_-]/g, ' ')));
  if (result.fullName >= 0) { result.lastName = -1; result.firstName = -1; }
  return result;
}
export function prepareRoster(rows, mapping, options = {}) {
  const { defaultClass = '', selectedClass = '', existing = [], pseudonymize = false, prefix = 'Élève' } = options;
  const get = (row, field) => Number.isInteger(mapping[field]) && mapping[field] >= 0 ? clean(row[mapping[field]]) : '';
  if (mapping.fullName < 0 && mapping.lastName < 0 && mapping.firstName < 0) throw Error('Choisissez une colonne de nom, prénom ou nom complet.');
  const mapped = Object.values(mapping).filter(i => i >= 0);
  if (new Set(mapped).size !== mapped.length) throw Error('Une colonne ne peut pas correspondre à deux champs.');
  if (mapping.fullName >= 0 && (mapping.lastName >= 0 || mapping.firstName >= 0)) throw Error('Choisissez soit le nom complet, soit les colonnes nom et prénom.');
  const entries = [], errors = [], classes = new Set();
  rows.forEach((row, index) => {
    const name = mapping.fullName >= 0 ? get(row, 'fullName') : [get(row, 'firstName'), get(row, 'lastName')].filter(Boolean).join(' ');
    const className = get(row, 'className') || clean(defaultClass);
    if (className) classes.add(className);
    if (selectedClass && selectedClass !== className) return;
    if (!name || !className || name.length > 120 || className.length > 80) { errors.push({ row: index + 1, reason: !name ? 'Nom manquant' : !className ? 'Classe manquante' : 'Nom ou classe trop long' }); return; }
    entries.push({ name, className });
  });
  const seen = new Set(existing.map(rosterKey)); let duplicates = 0;
  const unique = entries.filter(student => { const key = rosterKey(student); if (seen.has(key)) { duplicates++; return false; } seen.add(key); return true; });
  if (existing.length + unique.length > 5000) throw Error('L’espace est limité à 5 000 élèves. Sélectionnez une classe ou réduisez la liste.');
  let students = unique;
  if (pseudonymize) {
    const safePrefix = clean(prefix);
    if (!safePrefix || safePrefix.length > 100) throw Error('Préfixe du pseudonyme : de 1 à 100 caractères.');
    const used = new Set(existing.map(rosterKey)); let sequence = 1;
    students = unique.map(student => {
      let candidate;
      do { candidate = { name: `${safePrefix} ${String(sequence++).padStart(3, '0')}`, className: student.className }; } while (used.has(rosterKey(candidate)));
      used.add(rosterKey(candidate)); return candidate;
    });
  }
  return { students, duplicates, errors, classes: [...classes].sort((a,b) => a.localeCompare(b, 'fr')), sourceCount: rows.length };
}
